import { User } from '@nextforce/api'
import { ListItem, makeStyles } from '@rneui/themed'
import { t } from 'app/i18l'
import React, { useState } from 'react'
import { FlatList, Pressable } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { RoundedButton } from 'shared/components/rounded-button'
import { EmptyUserListView } from 'shared/components/user/empty_user_list'
import { UserAvatar } from 'shared/components/user/user_avatar'
import { TaskUserListScreenParams } from 'tasks/types'
import { CrewMetrics } from 'users/anytime_metrics/views'

interface Props {
  users: User[] | undefined
  noUsersNote?: string
  onPerformReassign: (user: User) => Promise<void>
  onPerformAssign: (user: User) => void
  type: TaskUserListScreenParams['type']
}

export function TaskUserListView({
  users,
  noUsersNote,
  onPerformReassign,
  type,
  onPerformAssign,
}: Props) {
  const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined)
  const styles = useStyles()

  function onSelectUser(user: User) {
    setSelectedUser(user)
  }

  if (!users) {
    return null
  }

  if (users.length === 0) {
    return <EmptyUserListView noUsersNote={noUsersNote} />
  }

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.container}>
      <FlatList
        style={styles.list}
        data={users}
        keyExtractor={item => String(item.id)}
        renderItem={({ item }) => (
          <UserRow
            user={item}
            onSelectUser={() => onSelectUser(item)}
            selected={selectedUser && item.id === selectedUser.id}
            isSingleSelectList
          />
        )}
        onEndReachedThreshold={1}
      />
      {type === 'reassign' ? (
        <RoundedButton
          title={t('reassign')}
          onPress={async () => {
            await onPerformReassign(selectedUser!)
          }}
          containerStyle={styles.buttonContainer}
          disabled={!selectedUser}
        />
      ) : type === 'assign' ? (
        <RoundedButton
          title={t('assign')}
          onPress={() => {
            onPerformAssign(selectedUser!)
          }}
          containerStyle={styles.buttonContainer}
          disabled={!selectedUser}
        />
      ) : null}
    </SafeAreaView>
  )
}

interface UserRowProps {
  user: User
  onSelectUser: () => void
  selected?: boolean
  isSingleSelectList?: boolean
}

function UserRow({
  user,
  onSelectUser,
  selected,
  isSingleSelectList,
}: UserRowProps) {
  const styles = useStyles()
  return (
    <Pressable onPress={onSelectUser}>
      <ListItem containerStyle={styles.listItem}>
        <UserAvatar user={user} size="medium" />
        <ListItem.Content>
          <ListItem.Title>{`${user.firstName} ${user.lastName}`}</ListItem.Title>
        </ListItem.Content>
        <CrewMetrics user={user} />
        {isSingleSelectList && (
          <ListItem.CheckBox
            onPress={onSelectUser}
            iconRight
            checkedIcon="dot-circle-o"
            uncheckedIcon="circle-o"
            checked={selected ?? false}
          />
        )}
      </ListItem>
    </Pressable>
  )
}

const useStyles = makeStyles(theme => ({
  container: {
    flex: 1,
    justifyContent: 'space-between',
    backgroundColor: theme.colors?.grey5,
  },
  list: {
    paddingVertical: 10,
  },
  listItem: {
    paddingVertical: 20,
    borderBottomWidth: 0.6,
    paddingLeft: 16,
    paddingRight: 0,
  },
  buttonContainer: {
    margin: 10,
  },
  subtitle: { color: theme.colors?.grey1 },
}))
